import { useEffect, useMemo, useState } from "react";
import AppLayout from "@/components/AppLayout";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Settings, Save, Info } from "lucide-react";

const DEFAULT_PLACEMENTS = ["1", "2", "3", "4", "5"];

export default function AdminRankingConfig() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [extraPlacement, setExtraPlacement] = useState("");
  const [extraValue, setExtraValue] = useState("");

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/ranking-config");
        setCfg(data);
      } catch (err) {
        console.error("[ranking-config] load:", err);
        toast.error("Falha ao carregar configuração");
      } finally { setLoading(false); }
    })();
  }, []);

  const setNum = (k, v) => setCfg((c) => ({ ...c, [k]: Number(v) || 0 }));
  const setPlacement = (pl, v) => setCfg((c) => ({
    ...c,
    placement_bonus: { ...(c.placement_bonus || {}), [pl]: Number(v) || 0 },
  }));
  const removePlacement = (pl) => setCfg((c) => {
    const next = { ...(c.placement_bonus || {}) };
    delete next[pl];
    return { ...c, placement_bonus: next };
  });
  const addPlacement = () => {
    if (!extraPlacement || !extraPlacement.match(/^\d+$/)) { toast.error("Colocação inválida"); return; }
    setPlacement(extraPlacement, extraValue || 0);
    setExtraPlacement(""); setExtraValue("");
  };

  const save = async () => {
    if (!cfg) return;
    setSaving(true);
    try {
      const payload = {
        points_per_present: Number(cfg.points_per_present) || 0,
        points_per_justified: Number(cfg.points_per_justified) || 0,
        points_per_absent: Number(cfg.points_per_absent) || 0,
        points_per_competition: Number(cfg.points_per_competition) || 0,
        placement_bonus: Object.fromEntries(
          Object.entries(cfg.placement_bonus || {}).map(([k, v]) => [String(k), Number(v) || 0])
        ),
        score_multiplier: Number(cfg.score_multiplier) || 0,
      };
      const { data } = await api.put("/ranking-config", payload);
      setCfg(data);
      toast.success("Configuração salva");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Falha ao salvar");
    } finally { setSaving(false); }
  };

  const placementKeys = useMemo(
    () => Object.keys(cfg?.placement_bonus || {}).sort((a, b) => Number(a) - Number(b)),
    [cfg]
  );

  if (loading || !cfg) {
    return <AppLayout kind="admin"><div className="text-slate-300">Carregando…</div></AppLayout>;
  }

  return (
    <AppLayout kind="admin">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl text-white font-semibold flex items-center gap-3">
            <Settings className="w-8 h-8 text-amber-400" />
            Configuração do Ranking
          </h1>
          <p className="text-slate-400 mt-1">Ajuste os pesos usados no cálculo do Ranking Interno GSX.</p>
        </div>
        <Button onClick={save} disabled={saving} data-testid="rc-save-btn"
          className="bg-amber-500 text-[#071A33] hover:bg-amber-400 font-semibold">
          <Save className="w-4 h-4 mr-2" /> {saving ? "Salvando…" : "Salvar Configuração"}
        </Button>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="bg-transparent gsx-card-dark p-5">
          <h3 className="text-white font-semibold mb-4">Frequência nas aulas</h3>
          <div className="grid sm:grid-cols-3 gap-3">
            <Num label="Pts por Presente" v={cfg.points_per_present} set={(v)=>setNum("points_per_present",v)} testid="rc-pts-present" />
            <Num label="Pts por Justificado" v={cfg.points_per_justified} set={(v)=>setNum("points_per_justified",v)} testid="rc-pts-justified" />
            <Num label="Pts por Ausente" v={cfg.points_per_absent} set={(v)=>setNum("points_per_absent",v)} testid="rc-pts-absent" />
          </div>
          <p className="text-[11px] text-slate-500 mt-3 flex items-start gap-1">
            <Info className="w-3 h-3 mt-0.5 text-amber-400 shrink-0" />
            Cada chamada pontua conforme o status. Padrão: 3 / 1 / 0.
          </p>
        </Card>

        <Card className="bg-transparent gsx-card-dark p-5">
          <h3 className="text-white font-semibold mb-4">Competições</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            <Num label="Pts por participar" v={cfg.points_per_competition} set={(v)=>setNum("points_per_competition",v)} testid="rc-pts-competition" />
            <Num label="Multiplicador do score" v={cfg.score_multiplier} set={(v)=>setNum("score_multiplier",v)} testid="rc-score-mult" step="0.1" />
          </div>
          <p className="text-[11px] text-slate-500 mt-3 flex items-start gap-1">
            <Info className="w-3 h-3 mt-0.5 text-amber-400 shrink-0" />
            Cada competição registrada soma "Pts por participar" + bônus por colocação + (score × multiplicador).
          </p>
        </Card>

        <Card className="bg-transparent gsx-card-dark p-5 lg:col-span-2">
          <h3 className="text-white font-semibold mb-4">Bônus por colocação</h3>
          <div className="grid sm:grid-cols-3 md:grid-cols-5 gap-3">
            {placementKeys.map(pl => (
              <div key={pl} className="space-y-2" data-testid={`rc-place-${pl}`}>
                <Label className="text-slate-300">{pl}º lugar</Label>
                <div className="flex gap-1">
                  <Input type="number" value={cfg.placement_bonus[pl]}
                    onChange={(e)=>setPlacement(pl, e.target.value)}
                    className="bg-[#0D2A52] border-amber-500/20 text-white" />
                  <Button size="icon" variant="ghost" onClick={()=>removePlacement(pl)}
                    className="text-red-400 hover:bg-red-500/10 h-9 w-9">×</Button>
                </div>
              </div>
            ))}
          </div>
          <div className="mt-5 flex gap-2 flex-wrap items-end">
            <div className="space-y-1">
              <Label className="text-slate-300 text-xs">Colocação (nº)</Label>
              <Input value={extraPlacement} onChange={(e)=>setExtraPlacement(e.target.value)}
                placeholder="Ex: 6" data-testid="rc-new-place"
                className="bg-[#0D2A52] border-amber-500/20 text-white w-32" />
            </div>
            <div className="space-y-1">
              <Label className="text-slate-300 text-xs">Pontos</Label>
              <Input type="number" value={extraValue} onChange={(e)=>setExtraValue(e.target.value)}
                placeholder="Ex: 3" data-testid="rc-new-place-pts"
                className="bg-[#0D2A52] border-amber-500/20 text-white w-32" />
            </div>
            <Button size="sm" onClick={addPlacement} data-testid="rc-add-place-btn"
              className="bg-amber-500/20 border border-amber-500/40 text-amber-300 hover:bg-amber-500/30 h-9">
              Adicionar colocação
            </Button>
            {!placementKeys.length && (
              <p className="text-[11px] text-slate-500">Sem bônus por colocação. Adicione se quiser recompensar pódios/top-N.</p>
            )}
          </div>
        </Card>

        <Card className="bg-transparent gsx-card-dark gsx-app-card-highlight p-5 lg:col-span-2 border-amber-500/40 bg-amber-500/5">
          <h3 className="text-amber-300 font-semibold mb-2">Fórmula usada</h3>
          <div className="font-mono text-xs sm:text-sm text-slate-200 space-y-1">
            <div>frequência = presentes×{cfg.points_per_present} + justificados×{cfg.points_per_justified} + ausentes×{cfg.points_per_absent}</div>
            <div>competições = Σ ( {cfg.points_per_competition} + bônus_colocação + score×{cfg.score_multiplier} )</div>
            <div className="text-amber-300 font-semibold">total = frequência + competições</div>
          </div>
        </Card>
      </div>
    </AppLayout>
  );
}

function Num({ label, v, set, testid, step }) {
  return (
    <div className="space-y-2">
      <Label className="text-slate-300">{label}</Label>
      <Input type="number" value={v ?? 0} step={step} data-testid={testid}
        onChange={(e)=>set(e.target.value)}
        className="bg-[#0D2A52] border-amber-500/20 text-white" />
    </div>
  );
}
